import { ListChecks } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSettings } from "@/hooks/use-settings";
import { useRunPendingTasks } from "@/hooks/use-app-tasks";
import { useAuthStore } from "@/stores/auth.store";
import { Button } from "@/components/ui/button";

// ═══════════════════════════════════════════════════════════
// PendingTasksBanner — App-wide admin alert when there are
// one-time data tasks that have not yet run for this tenant.
//
// Only visible to admins. Dismissed only when all tasks complete —
// never dismissible by click. Piggybacks on the existing useSettings()
// fetch so no additional API call is needed.
// ═══════════════════════════════════════════════════════════

export function PendingTasksBanner() {
  const { t } = useTranslation("common");
  const isAdmin = useAuthStore((s) => s.isAdmin);
  const { data: settings } = useSettings();
  const runTasks = useRunPendingTasks();

  // Only show to admins with loaded settings that have pending tasks
  if (!isAdmin || !settings) return null;
  if ((settings.pendingTaskIds?.length ?? 0) === 0) return null;

  const count = settings.pendingTaskIds.length;

  return (
    <div className="flex flex-col gap-1 border-b border-amber-500/30 bg-amber-500/10 px-6 py-3 sm:flex-row sm:items-center sm:gap-3">
      <div className="flex items-center gap-2 shrink-0">
        <ListChecks className="h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
        <span className="text-sm font-semibold text-amber-700 dark:text-amber-400">
          {t("taskBanner.title")}
        </span>
      </div>
      <p className="flex-1 text-sm text-amber-600 dark:text-amber-500">
        {t("taskBanner.desc", { count })}
      </p>
      <div className="flex items-center gap-2 shrink-0">
        <Button
          size="sm"
          variant="outline"
          className="border-amber-500/50 text-amber-700 hover:bg-amber-500/20 dark:text-amber-400 dark:hover:bg-amber-500/20"
          disabled={runTasks.isPending}
          onClick={() => {
            runTasks.mutate();
          }}
          aria-label={t("taskBanner.runNow")}
        >
          <ListChecks className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
          {runTasks.isPending ? t("taskBanner.running") : t("taskBanner.runNow")}
        </Button>
      </div>
    </div>
  );
}
